import { useToken } from './auth/token';
import { useIsMobile } from '@utils/CalVW.tsx';
import { useMyProfile } from './user/myprofile';
import useSWR from 'swr';
import API from '@/utils/api';
import { useExchangeStore } from './trade/exchangeStore';
import BigNumber from 'bignumber.js';

export enum USER_LOG_TYPE {
  LOGIN = '로그인',
  LOGOUT = '로그아웃',
  KYC_REQUEST = 'KYC인증 요청',

  ORDER_OPEN = '포지션 진입',
  ORDER_CLOSE = '포지션 청산',
  ORDER_CLOSE_ALL = '포지션 전체 청산',

  TRANSFER_FUTURE = '선물 지갑으로 전송',
  TRANSFER_SPOT = '현물 지갑으로 전송',

  TP_SL_ADD = '[TP/SL] 추가',
  TP_SL_DELETE = '[TP/SL] 삭제',

  UNLOAD = '페이지 나감',

  // BOOKMARK_ADD = "코인 즐겨찾기 추가",
  // BOOKMARK_REMOVE = "코인 즐겨찾기 삭제",
}

export interface UserLog {
  log: string;
  data: any;
  userId?: number;
  token?: string;
}

const USER_LOG_KEY = '/system/log';

export const useUserLogger = () => {
  const { EPs } = useExchangeStore();
  const { data: user } = useMyProfile();
  const { token } = useToken();
  const isMobile = useIsMobile();

  const { data: userIp } = useSWR(
    'getUserIp',
    async () => {
      const response = await fetch('https://api.ipify.org/?format=json');
      return response.json();
    },
    {
      revalidateOnFocus: true,
      revalidateOnMount: true,
    }
  );

  const mutateUserLog = async (data: UserLog) => {
    try {
      // ✅ 필수 데이터 검증 (토큰, 유저ID)
      const logToken = data.token ?? token;
      const userId = data.userId ?? user?.id;

      if (!logToken || !userId) {
        console.warn('UserLog skipped: missing token or userId', {
          hasToken: !!logToken,
          hasUserId: !!userId,
        });
        return null;
      }

      const response = await API.postNoLogout(USER_LOG_KEY, logToken, {
        userid: userId,
        log: data.log,
        data: JSON.stringify(data.data),
        platform: isMobile ? 'M' : 'W',
        ip: userIp?.ip,
      });

      return response;
    } catch (error) {
      // ✅ 로깅 실패는 무시 (앱 동작에 영향 안줌)
      console.error('UserLog error (ignored):', error);
      return null;
    }
  };

  const getTpSlLog = (data: any, logType: USER_LOG_TYPE) => {
    try {
      if (logType === USER_LOG_TYPE.TP_SL_ADD) {
        return `${logType} ${data?.posId}번 주문 ${data?.tp ? `TP ${new BigNumber(data.tp).toFormat()}` : ''} ${data?.sl ? `SL ${new BigNumber(data.sl).toFormat()}` : ''}`;
      } else {
        return `${logType} ${data?.posId}번 주문`;
      }
    } catch (e) {
      return 'log error';
    }
  };

  const getOrderLog = (data: any, logType: USER_LOG_TYPE) => {
    try {
      const { pairs, dp } =
        EPs?.find(
          (ep) => (ep.id || ep.ep_id) === (data.ep_id || data.exchange_pairs_id)
        ) || {};
      const coin = pairs?.split('/')[0] || '';
      const isRemarks = data?.is_triggered || data?.tpsl;
      return `[주문] ${logType} ${coin} ${data?.leverage ? `${data?.leverage}X` : ''} ${(data?.long_short || data?.longshort) === 'L' ? '롱' : '숏'} ${data?.limit_market === 'L' ? '지정가' : '시장가'}
            ${data?.price ? ` ${new BigNumber(data.price).toFormat(dp || 4, BigNumber.ROUND_DOWN)}` : ''}
            ${data?.amount ? ` ${new BigNumber(data.amount * (data.leverage || 1)).toFormat(4, BigNumber.ROUND_DOWN)} ${coin}` : ''}${isRemarks ? ` ${`비고(${data?.is_triggered ? (data?.tpsl ? '트리거 ' : '트리거') : ''}${data?.tpsl || data?.tp || data?.sl ? 'TP/SL' : ''})`}` : ''}`;
    } catch (e) {
      return 'log error';
    }
  };

  return {
    userIp,
    mutateUserLog,
    getOrderLog,
    getTpSlLog,
  };
};
